import {
	Avatar,
	Button,
	Checkbox,
	ComboBox,
	Detail,
	FlexRow,
	Label,
	Link,
	Modal,
	Text,
	TextField,
} from '@forecast-it/design-system';
import {noop} from 'lodash';
import React, {useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {REPORT_TYPE, ReportType} from '../../../../../constants';
import {profilePicRectSrc} from '../../../../../directApi';
import ChangeAccessForSavedReportMutation from '../../../../../mutations/change_access_for_saved_report_mutation';
import CreateReportShareMutation from '../../../../../mutations/create_report_share_mutation';
import DeleteReportShareMutation from '../../../../../mutations/delete_report_share_mutation';
import SetReportSharePermissionMutation from '../../../../../mutations/set_report_share_permission_mutation';
import {createToast} from '../../../../shared/components/toasts/another-toast/toaster';
import {isInValidEmailFormat} from '../../../../shared/util/EmailUtil';
import Util from '../../../../shared/util/util';
import {parseServerRejection} from './reportShareRejection';
import {
	AlreadySharedContainer,
	FlexContainer,
	SharedEmailContainer,
	SharedPersonInfoWrapper,
	SharedPersonRow,
	SharedPersonTextColumn,
} from './ShareReportModal_style';
import {Person, ReportShare, SharePermissionType} from './ShareReportModalTypes';

type ShareReportModalProps = {
	closeModal: () => void;
	reportSharesInput: ReportShare[];
	reportId: string;
	privateAccessInput: boolean;
	reportOwner: Person;
	reportType: ReportType;
	/** Opt in to the per-share viewer/editor role control. Off by default: the standard reports
	 * share flow has no role concept and must keep rendering exactly as it does without it. */
	enableEditorRoles?: boolean;
	/** Notified with the report's NEW privacy value once the "share with company" toggle commits
	 * successfully. Optional and a no-op by default: the standard Reports-list share flow has no
	 * caller that needs this and must keep behaving exactly as it does without one. */
	onPrivateAccessChange?: (privateAccess: boolean) => void;
	/** Resolves a Forecast person for a share's email so a share added while this modal is open can
	 * render the same avatar/name block as a share that was already present when it opened, instead
	 * of falling back to the bare email until the dialog is closed and reopened. Optional and
	 * resolves to nothing by default: the standard Reports-list share flow has no person data to
	 * offer and must keep behaving exactly as it does without one. */
	resolvePerson?: (email: string) => Person | undefined;
};

/** The avatar + name + email block used for the report owner. Reused for a share once it has been
 * matched to a Forecast person, so a shared row that resolves to a real person looks the same as
 * the owner row instead of the plain-email fallback. */
const SharedPersonSummary = ({person}: {person: Person}): React.JSX.Element => (
	<SharedPersonRow>
		<Avatar
			src={person.profilePictureId ? profilePicRectSrc(person.profilePictureId) : undefined}
			size={'m'}
			name={person.fullName}
		/>
		<SharedPersonTextColumn>
			{/* `Text` carries the design system's own single-line truncation, with a tooltip that
			    appears only once the name is actually cut off. `Detail` has neither, so its
			    truncation comes from the column's CSS and it falls back to a `title`. */}
			<Text size={'2'} ellipsis tooltipOnEllipsis>
				{person.fullName}
			</Text>
			<Detail size={'1'} noWrap title={person.email}>
				{person.email}
			</Detail>
		</SharedPersonTextColumn>
	</SharedPersonRow>
);

const ShareReportModal = ({
	closeModal,
	reportSharesInput,
	reportId,
	privateAccessInput,
	reportOwner,
	reportType,
	enableEditorRoles = false,
	onPrivateAccessChange = noop,
	resolvePerson = () => undefined,
}: ShareReportModalProps): React.JSX.Element => {
	const intl = useIntl();
	const [disableAddButton, setDisableAddButton] = useState<boolean>(true);
	const [inputErrorMessage, setInputErrorMessage] = useState<string | null>(null);
	const [reportShares, setReportShares] = useState<ReportShare[]>(reportSharesInput);
	const [newEmail, setNewEmail] = useState<string>('');
	const [privateAccess, setPrivateAccess] = useState<boolean>(privateAccessInput);

	const nonShareableReportTypes: ReportType[] = [REPORT_TYPE.UTILIZATION_REPORT, REPORT_TYPE.PROJECT_PORTFOLIO_REPORT];
	const canShareExternally = !nonShareableReportTypes.includes(reportType);

	const emailExistsInAlreadySharedPersonsList = (email: string) => {
		return reportShares.some(reportShare => reportShare.email === email);
	};

	const validateInput = (input: string) => {
		if (emailExistsInAlreadySharedPersonsList(input)) {
			setInputErrorMessage('The report has been already shared with this email.');
			setDisableAddButton(true);
		} else if (isInValidEmailFormat(input)) {
			setInputErrorMessage(null);
			setDisableAddButton(false);
		} else {
			setInputErrorMessage('You have entered an invalid email address.');
			setDisableAddButton(true);
		}
		setNewEmail(input);
	};

	const togglePrivateAccess = () => {
		const nextPrivateAccess = !privateAccess;
		const onSuccess = () => {
			if (privateAccess) {
				createToast({
					duration: 5000,
					message: intl.formatMessage({id: 'shared_report.report_shared_with_organisation'}),
				});
			} else {
				createToast({
					duration: 5000,
					message: intl.formatMessage({id: 'shared_report.report_made_private'}),
				});
			}

			setPrivateAccess(nextPrivateAccess);
			onPrivateAccessChange(nextPrivateAccess);
		};
		Util.CommitMutation(
			ChangeAccessForSavedReportMutation,
			{
				id: reportId,
				privateAccess: nextPrivateAccess,
			},
			onSuccess
		);
	};

	const addReportShares = createSharesResponse => {
		const newReportShares = createSharesResponse.map(response => {
			// The user's email is nullable in the schema, and this runs inside the create mutation's
			// success callback - a throw here would be swallowed after the success toast has already
			// fired, leaving the user with a confirmation and no row. Coerce to '' exactly as the
			// builder does when it matches the shares that were already present.
			const email = response.node.user.email ?? '';
			return {
				email,
				sharedId: response.node.id,
				userId: response.node.user.id,
				permissionType: 'VIEWER' as const,
				isInternal: response.node.user.isInternal ?? false,
				person: resolvePerson(email),
			};
		});
		setReportShares([...reportShares, ...newReportShares]);
	};

	const removeReportShare = (deletedShareId: string) => {
		const filteredReportShares = reportShares.filter(reportShare => reportShare.sharedId !== deletedShareId);
		setReportShares([...filteredReportShares]);
	};

	const handleCreateShare = () => {
		Util.CommitMutation(
			CreateReportShareMutation,
			{
				reportId: reportId,
				emails: [newEmail],
			},
			data => {
				if (data.createReportShare.warnings?.length) {
					const emails = data.createReportShare.warnings
						.map(warning => warning.substring(warning.indexOf(':') + 1))
						.join(',');

					createToast({
						duration: 5000,
						message: intl.formatMessage({id: 'shared_report.person_added.error'}) + ' ' + emails,
					});
				} else {
					createToast({
						duration: 5000,
						message: intl.formatMessage({id: 'shared_report.report_shared_with_person'}, {email: newEmail}),
					});
				}
				setNewEmail('');
				setDisableAddButton(true);
				addReportShares(data.createReportShare.shareEdges);
			},
			true,
			null,
			(transaction: unknown, onFailure: (transaction: unknown) => boolean | void) => {
				const rejected = parseServerRejection(String(transaction));
				if (!rejected) {
					// Not a reason the server stated, so the standard flow decides what it is: it retries
					// a transient network failure - which never carries the marker, so retrying is never
					// pre-empted here - and otherwise shows the correlation-ID / auth error handling the
					// rest of the app relies on.
					onFailure(transaction);
					return;
				}
				// A refusal the server explained, so the standard flow is deliberately not run: it has
				// no message field and would stack an opaque modal over the dialog holding the address
				// the user typed. The reason is shown verbatim - report-service owns this copy, and
				// restating it here would let the two drift.
				setInputErrorMessage(rejected.reason);
				setDisableAddButton(true);
			}
		);
	};

	const handleDeleteReportShare = (sharedId: string) => {
		Util.CommitMutation(
			DeleteReportShareMutation,
			{
				id: sharedId,
				reportId: reportId,
			},
			data => {
				removeReportShare(data.deleteReportShare.deletedShareId);
				createToast({
					duration: 5000,
					message: intl.formatMessage({id: 'shared_report.person_removed'}),
				});
			}
		);
	};

	const isSharePermissionType = (value: unknown): value is SharePermissionType => value === 'VIEWER' || value === 'EDITOR';

	const handlePermissionChange = (share: ReportShare, permission: unknown) => {
		// The control itself is disabled for non-internal shares, but that is a UI affordance, not
		// the invariant: only a Forecast user of the company may ever be granted editor rights.
		if (share.isInternal !== true) {
			return;
		}
		// The design system combobox calls this with null when its text input is cleared. Ignore
		// anything that isn't one of the two real roles rather than sending it to the backend.
		if (!isSharePermissionType(permission)) {
			return;
		}
		if (!share.userId) {
			return;
		}
		const previous = share.permissionType ?? 'VIEWER';
		setReportShares(current =>
			current.map(row => (row.sharedId === share.sharedId ? {...row, permissionType: permission} : row))
		);
		Util.CommitMutation(
			SetReportSharePermissionMutation,
			{reportId, userId: share.userId, permission},
			() => {
				createToast({
					duration: 5000,
					message: intl.formatMessage({id: 'report.share_role_changed'}),
				});
			},
			true,
			null,
			(transaction: unknown, onFailure: (transaction: unknown) => boolean | void) => {
				// Defer to the standard flow first: it retries a transient network failure and, once an
				// attempt is truly final, shows the correlation-ID / auth error handling the rest of the
				// app relies on. Only revert the optimistic role once we know this attempt will not be
				// retried - a pending retry may still succeed.
				const isRetrying = onFailure(transaction);
				if (isRetrying) return;

				// Never leave a role on screen the backend did not accept.
				setReportShares(current =>
					current.map(row => (row.sharedId === share.sharedId ? {...row, permissionType: previous} : row))
				);
			}
		);
	};

	return (
		<Modal size={Modal.SIZE.MEDIUM} onCloseModal={closeModal}>
			<Modal.Title>
				<FormattedMessage id={'report.share'} />
			</Modal.Title>
			<Modal.Content>
				{canShareExternally ? (
					<>
						<Label>{intl.formatMessage({id: 'report.share-input-label'})}</Label>
						{inputErrorMessage && <Text color={Text.Color.error}>{inputErrorMessage}</Text>}
						<FlexContainer>
							<FlexRow>
								<TextField
									value={newEmail}
									onChange={e => {
										validateInput(e.target.value);
									}}
									data-cy="share-email"
								/>
								<Button
									data-cy="add-button"
									emphasis={'medium'}
									onPress={handleCreateShare}
									disabled={disableAddButton}
								>
									{intl.formatMessage({id: 'common.add'})}
								</Button>
							</FlexRow>
						</FlexContainer>
						<AlreadySharedContainer>
							<SharedPersonInfoWrapper>
								<SharedPersonSummary person={reportOwner} />
								<Text>Owner</Text>
							</SharedPersonInfoWrapper>
							{[...reportShares]
								.sort((rs1, rs2) => rs1.email.localeCompare(rs2.email))
								.map(reportShare => (
									<SharedPersonInfoWrapper key={reportShare.sharedId} data-cy="share-row">
										{reportShare.person ? (
											<SharedPersonSummary person={reportShare.person} />
										) : (
											<SharedEmailContainer>
												{/* The column truncates, so carry the whole address in `title`:
												    for a recipient with no matching person the email is the only
												    thing identifying the row, and a clipped one is unreadable. */}
												<Detail size={'1'} noWrap title={reportShare.email}>
													{reportShare.email}
												</Detail>
											</SharedEmailContainer>
										)}
										{/* The role column states its own width. A `FlexRow` defaults to `flex: 1`, so it
										    both grows and shrinks from a zero basis; left at that default it is the item
										    that gives when the column beside it asks for the row, and the combo box input
										    collapses to a few pixels - the selected role becomes invisible and the row
										    renders as a bare chevron. */}
										{enableEditorRoles ? (
											<FlexRow flex={'0 0 auto'} alignItems={'center'} gap={'s'}>
												<ComboBox
													data-cy={`share-role-${reportShare.sharedId}`}
													aria-label={intl.formatMessage({id: 'report.share_role'})}
													value={
														reportShare.isInternal === true
															? reportShare.permissionType ?? 'VIEWER'
															: 'VIEWER'
													}
													disabled={reportShare.isInternal !== true}
													isDisabled={reportShare.isInternal !== true}
													width={'flex'}
													onChange={key => handlePermissionChange(reportShare, key)}
												>
													<ComboBox.Item
														key="VIEWER"
														textValue={intl.formatMessage({id: 'report.share_role_viewer'})}
													>
														<Text>{intl.formatMessage({id: 'report.share_role_viewer'})}</Text>
													</ComboBox.Item>
													<ComboBox.Item
														key="EDITOR"
														textValue={intl.formatMessage({id: 'report.share_role_editor'})}
													>
														<Text>{intl.formatMessage({id: 'report.share_role_editor'})}</Text>
													</ComboBox.Item>
												</ComboBox>
												<Link size={'2'} onClick={() => handleDeleteReportShare(reportShare.sharedId)}>
													{intl.formatMessage({id: 'common.remove'})}
												</Link>
											</FlexRow>
										) : (
											<Link size={'2'} onClick={() => handleDeleteReportShare(reportShare.sharedId)}>
												{intl.formatMessage({id: 'common.remove'})}
											</Link>
										)}
									</SharedPersonInfoWrapper>
								))}
						</AlreadySharedContainer>
					</>
				) : null}
				<Label>{intl.formatMessage({id: 'report.share-company-label'})}</Label>
				<FlexRow>
					<Checkbox data-cy={'access-checkbox'} checked={!privateAccess} onChange={togglePrivateAccess}></Checkbox>
					<Text>{intl.formatMessage({id: 'report.private_access.description'})}</Text>
				</FlexRow>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={closeModal} dataCy={'modal-btn-done'}>
					<FormattedMessage id="common.done" />
				</Modal.PrimaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export default ShareReportModal;
